/**
 * Validation Context Builder
 *
 * Helper utilities to build FamilyTreeGraphContext from canvas data.
 * This bridges the gap between UI data structures and validation layer.
 */

import type { TreeCanvasData } from '@/types/tree-dashboard.types';
import type { FamilyTreeGraphContext } from './types';

/**
 * Build graph context from tree canvas data
 *
 * @param canvasData - Canvas data from useTreeCanvas hook
 * @returns Graph context ready for validation
 *
 * @example
 * ```typescript
 * const context = buildGraphContext(canvasData);
 * const result = validateMutation(mutation, context);
 * ```
 */
export function buildGraphContext(canvasData: TreeCanvasData): FamilyTreeGraphContext {
  return {
    persons: canvasData.persons,
    relationships: canvasData.relationships,
  };
}

/**
 * Check if graph context is valid for validation
 *
 * @param context - Graph context to validate
 * @returns True if context has required data
 */
export function isValidGraphContext(context: FamilyTreeGraphContext | null | undefined): context is FamilyTreeGraphContext {
  return !!(context?.persons && context?.relationships);
}
